import { format } from 'date-fns';
import { escapeHtml } from '../template/index.js';
export const FieldType = {
    TEXT: 'TEXT',
    TEXTAREA: 'TEXTAREA',
    SELECT: 'SELECT',
    MULTI_SELECT: 'MULTI_SELECT',
    CHECKBOX: 'CHECKBOX',
    CHECKBOX_GROUP: 'CHECKBOX_GROUP',
    DATE: 'DATE',
    TIME: 'TIME',
    NUMBER: 'NUMBER',
    CURRENCY: 'CURRENCY',
    SUGGESTED_TEXT: 'SUGGESTED_TEXT',
    RICH_TEXT: 'RICH_TEXT',
};
const NAMED_HTML_ENTITIES = {
    amp: '&',
    apos: "'",
    gt: '>',
    lt: '<',
    nbsp: ' ',
    quot: '"',
};
const MAX_CODE_POINT = 0x10ffff;
function decodeHtmlEntities(text) {
    return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, entity) => {
        if (!entity.startsWith('#')) {
            return NAMED_HTML_ENTITIES[entity.toLowerCase()] ?? match;
        }
        const isHex = entity[1]?.toLowerCase() === 'x';
        const codePoint = isHex ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10);
        if (Number.isNaN(codePoint) || codePoint > MAX_CODE_POINT) {
            return match;
        }
        return String.fromCodePoint(codePoint);
    });
}
export function richTextToPlainText(html) {
    const text = html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<(li|hr)\b[^>]*>/gi, '\n')
        .replace(/<\/(p|h[1-6]|blockquote|pre|div|ul|ol)>/gi, '\n')
        .replace(/<[^>]*>/g, '');
    return decodeHtmlEntities(text)
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
function plainTextToRichText(text) {
    return text
        .split(/\r?\n/)
        .map((line) => `<p>${escapeHtml(line)}</p>`)
        .join('');
}
function toSingleLine(text) {
    return text.replace(/\s*\r?\n\s*/g, ' ').trim();
}
/**
 * Converts a stored value between the text-based custom field types (TEXT, TEXTAREA, RICH_TEXT).
 * Returns `null` when the converted value has no content.
 */
export function convertTextCustomFieldValue(value, fromType, toType) {
    if (fromType === toType) {
        return value;
    }
    const plainText = fromType === FieldType.RICH_TEXT ? richTextToPlainText(value) : value;
    if (plainText.trim() === '') {
        return null;
    }
    switch (toType) {
        case FieldType.TEXT:
            return toSingleLine(plainText);
        case FieldType.RICH_TEXT:
            return plainTextToRichText(plainText.trim());
        default:
            return plainText;
    }
}
export function formatCustomFieldValue(value, type, emptyValue = 'Not provided') {
    if (value === null || value === undefined || value === '') {
        return emptyValue;
    }
    switch (type) {
        case FieldType.TEXT:
        case FieldType.TEXTAREA:
        case FieldType.SUGGESTED_TEXT:
            return String(value);
        case FieldType.NUMBER:
            return Number(value).toLocaleString('en-US');
        case FieldType.CURRENCY:
            return Number(value).toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
            });
        case FieldType.DATE:
            return format(new Date(String(value)), 'MMM d, yyyy');
        case FieldType.TIME:
            return String(value);
        case FieldType.RICH_TEXT:
            return richTextToPlainText(String(value)) || emptyValue;
        case FieldType.CHECKBOX:
            return value ? 'Yes' : 'No';
        case FieldType.SELECT:
            return String(value);
        case FieldType.MULTI_SELECT:
            if (Array.isArray(value)) {
                return value.length > 0 ? value.join(', ') : emptyValue;
            }
            return String(value);
        case FieldType.CHECKBOX_GROUP: {
            // Value is stored as { "Option A": true, "Option B": false, ... }
            const selected = Object.entries(value)
                .filter(([, checked]) => checked)
                .map(([label]) => label);
            return selected.length > 0 ? selected.join(', ') : emptyValue;
        }
        default:
            return String(value);
    }
}
